"use client";

import { memo, useCallback } from "react";
import Image from "next/image";
import { Card } from "@/components/ui/card";
import { Typography } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { PreferenceIconName } from "../book-for-me.type";
import {
  IconAirConditioning,
  IconBarbell,
  IconCalendarCheck,
  IconCoffee,
  IconFileText,
  IconMessageDots,
  IconParking,
  IconPaw,
  IconPool,
  IconSearch,
  IconWifi,
} from "@tabler/icons-react";

/**
 * 1. StepHeader
 */
export type StepHeaderProps = {
  step: number;
  title: string;
  subtitle: string;
};

export const StepHeader = ({ step, title, subtitle }: StepHeaderProps) => (
  <div className="flex items-start gap-3">
    <Typography
      variant="h4"
      className="bg-primary-muted grid size-10 items-center justify-center rounded-full text-primary"
    >
      {step}
    </Typography>

    <div className="space-y-0.5">
      <Typography variant="subtitle1" mdVariant="h6">
        {title}
      </Typography>
      <Typography variant="body2">{subtitle}</Typography>
    </div>
  </div>
);

/**
 * 2. BookForMeHelpBanner
 */
export interface BookForMeHelpBannerProps {
  className?: string;
  telegramUrl?: string;
  onTelegramClick?: () => void;
}

export const BookForMeHelpBanner = memo(function BookForMeHelpBanner({
  className,
  telegramUrl = "https://t.me/tovtrip",
  onTelegramClick,
}: BookForMeHelpBannerProps) {
  const handleTelegramClick = useCallback(() => {
    if (onTelegramClick) {
      onTelegramClick();
      return;
    }
    window.open(telegramUrl, "_blank", "noopener,noreferrer");
  }, [telegramUrl, onTelegramClick]);

  return (
    <section>
      <Card
        className={cn(
          "mt-4 flex flex-col items-start justify-between gap-4 rounded-3xl border-none bg-warning-background p-4 sm:flex-row sm:items-center",
          className,
        )}
      >
        <div className="flex items-center gap-2">
          <Image
            src="/social/glossy_golden_notification_bell.svg"
            height={66}
            width={66}
            alt="notification-glossy-bell"
          />

          <div>
            <Typography variant="subtitle1">
              Need help with your request?
            </Typography>
            <Typography variant="body1" className="max-w-fit">
              Our team is here to assist you. You can also contact us anytime.
            </Typography>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="md"
          onClick={handleTelegramClick}
          className="bg-white "
        >
          <Image
            src="/social/telegram.svg"
            height={24}
            width={24}
            alt="telegram"
          />
          <Typography variant="button-sm" className="font-semibold">
            Chat with us on Telegram
          </Typography>
        </Button>
      </Card>
    </section>
  );
});

/**
 * 3. Preference Icons
 */
const PREFERENCE_ICON_TUPLES = [
  ["coffee", IconCoffee],
  ["pool", IconPool],
  ["wifi", IconWifi],
  ["paw", IconPaw],
  ["parking", IconParking],
  ["ac", IconAirConditioning],
  ["barbell", IconBarbell],
] as const;

export const renderPreferenceIcon = (
  name: PreferenceIconName | string,
  className?: string,
) => {
  const Icon = PREFERENCE_ICON_TUPLES.find(([key]) => key === name)?.[1];
  if (!Icon) return null;

  return (
    <Icon className={cn("size-4 shrink-0 text-body", className)} stroke={2} />
  );
};

/**
 * 4. Step Icon
 */
const STEP_ICON_TUPLES = [
  ["file", IconFileText],
  ["search", IconSearch],
  ["chat", IconMessageDots],
  ["check", IconCalendarCheck],
] as const;

export const renderStepIcon = (iconName: string) => {
  const Icon =
    STEP_ICON_TUPLES.find(([name]) => name === iconName)?.[1] ?? IconFileText;
  return <Icon className="size-4.5 text-[#00C2E8]" stroke={2} />;
};
